import { createContext, useContext, ReactNode, useState } from 'react';

import { Environment } from '../../constants/environments';
import { HttpVerb } from '../../constants/httpVerbs';
import { membershipRuleKeys } from '../../constants/membership';
import {
  ErrorType,
  Membership,
  RequestMembershipData,
  ResponseHalfEntry,
  ResponseMembership
} from '../../types/membership';
import { Ticket } from '../../types/ticket';
import { logger } from '../../utils/logger';
import { documentMask } from '../../utils/maskValue';
import { normalizeDocument, normalizePasskey } from '../../utils/normalizeMembership';
import { useApiService } from '../useApiService';
import useRules from '../useRules';
import { useI18nContext } from './i18n.context';
import { useLoaderScreenContext } from './loader-screen.context';

type MembershipContextType = {
  membershipApplied: Membership | null;
  fetchingMembership: boolean;
  membershipError: string;
  changeFetchingMembership: (active: boolean) => void;
  changeMembershipError: (error: string) => void;
  changeMembershipApplied: (newMembership: Membership | null) => void;
  verifyMembership: (document: string, producerId: number) => Promise<{
    hasMembership: boolean,
    passkey: string,
    membership: Membership,
  }>;
  verifyThisMembershipHasTicket: (ticketGroups: Ticket[], membership: Membership) => boolean;
  verifyHalfEntry: (document: string, producerId: number) => Promise<boolean>;
  removeMembership: () => void;
  handleMembershipError: (document: string, errType: ErrorType) => void;
  handleMembershipLoadings: (active: boolean) => void;
};

export const MembershipContext = createContext<MembershipContextType>({} as MembershipContextType);

export function MembershipProvider({ children }: { children: ReactNode }) {
  const [fetchingMembership, setFetchingMembership] = useState(false);
  const [membershipError, setMembershipError] = useState('');
  const [membershipApplied, setMembershipApplied] = useState<Membership | null>(null);

  const { request } = useApiService();
  const { toggleLoader } = useLoaderScreenContext();
  const { translate } = useI18nContext();
  const { applyMembershipValidationRules } = useRules();

  const changeMembershipApplied = (newMembership: Membership | null) => {
    setMembershipApplied(newMembership);
  };

  const changeFetchingMembership = (active: boolean) => {
    setFetchingMembership(active);
  };

  const changeMembershipError = (error: string) => {
    setMembershipError(error);
  };

  const makeMembershipDicError = (document: string) => ({
    noMembership : `${translate(['INFORMATION', 'DOCUMENT'])}` +
      ` "${documentMask(document)}" ${translate(['INFORMATION', 'NO_MEMBER'])}`,
    membershipNotFound : `${translate(['INFORMATION', 'DOCUMENT'])}` +
      ` "${documentMask(document)}" ${translate(['INFORMATION', 'FAN_MEMBER_NOT_FOUND'])}`,
    inactivePlan : `${translate(['INFORMATION', 'DOCUMENT'])}` +
      ` "${documentMask(document)}" ${translate(['INFORMATION', 'INACTIVE_PLAN'])}`,
    withoutTickets : `${translate(['INFORMATION', 'DOCUMENT'])}` +
      ` "${documentMask(document)}" ${translate(['INFORMATION', 'FAN_MEMBER_WITHOUT_TICKET'])}`,
  });

  const makeVerifyMembershipReturnedDic = (membershipValue = {} as Membership) => ({
    failed : {
      hasMembership : false,
      passkey : '',
      membership : {} as Membership,
    },
    success : {
      hasMembership : true,
      passkey : normalizePasskey(membershipValue?.name ?? ''),
      membership : membershipValue,
    }
  });

  const handleMembershipLoadings = (active: boolean) => {
    changeFetchingMembership(active);
    toggleLoader({ show : active });
  };

  const handleMembershipError = (
    document: string,
    errType: ErrorType,
  ) => {
    const errorValue = makeMembershipDicError(document)[errType];
    changeMembershipApplied(null);
    changeMembershipError(errorValue);
    handleMembershipLoadings(false);
  };

  const verifyThisMembershipHasTicket = (ticketGroups: Ticket[], membership: Membership) => {
    const feedbackErrorToBeApplied = applyMembershipValidationRules({
      membershipResponse : null,
      tickets : ticketGroups,
      membership,
    });

    if (feedbackErrorToBeApplied !== membershipRuleKeys.success)
      handleMembershipError(membership.document, feedbackErrorToBeApplied);

    handleMembershipLoadings(false);
    return true;
  };

  const fetchMembership = async (document: string, producerId: number) => {
    handleMembershipLoadings(true);
    const normalizedDocument = normalizeDocument(document);
    const apiKey = Environment.API_KEY;

    try {
      const { detail : membershipResponse } = await request<RequestMembershipData, ResponseMembership>(
        {
          requestOptions : {
            resourcePath : `membership/verify-v2?legacyStyle=true&apikey=${apiKey}`,
            data : { document : normalizedDocument, producerId },
          },
          type : HttpVerb.POST,
        },
        { baseUrl : Environment.API_TICKETS_URL }
      );

      return membershipResponse;
    } catch (error) {
      logger.error(error as string);
      return null;
    }
  };

  const verifyMembership = async (document: string, producerId: number) => {
    const membershipResponse = await fetchMembership(document, producerId);
    const feedbackErrorToBeApplied = applyMembershipValidationRules({ membershipResponse, membership : null });

    if (feedbackErrorToBeApplied !== membershipRuleKeys.success) {
      handleMembershipError(document, feedbackErrorToBeApplied);
      return makeVerifyMembershipReturnedDic().failed;
    }

    const membershipValue = membershipResponse?.memberships[0] as Membership;
    changeMembershipApplied(membershipValue);
    return makeVerifyMembershipReturnedDic(membershipValue).success;
  };

  const verifyHalfEntry = async (document: string, producerId: number) => {
    const normalizedDocument = normalizeDocument(document);
    const apiKey = Environment.API_KEY;

    try {
      const { detail : halfEntryData } = await request<
        RequestMembershipData,
        ResponseHalfEntry
      >(
        {
          requestOptions : {
            resourcePath : `membership/verify-meia-entrada?legacyStyle=true&apikey=${apiKey}`,
            data : { document : normalizedDocument, producerId },
          },
          type : HttpVerb.POST,
        },
        { baseUrl : Environment.API_TICKETS_URL }
      );

      return halfEntryData.isArenaMember;
    } catch (error) {
      logger.error(error as string);
      return false;
    } finally {
      changeFetchingMembership(false);
    }
  };

  const removeMembership = () => {
    changeMembershipApplied(null);
    changeFetchingMembership(false);
  };

  const shared = {
    changeFetchingMembership,
    changeMembershipError,
    changeMembershipApplied,
    fetchingMembership,
    membershipError,
    membershipApplied,
    verifyMembership,
    verifyThisMembershipHasTicket,
    verifyHalfEntry,
    removeMembership,
    handleMembershipError,
    handleMembershipLoadings,
  };

  return (
    <MembershipContext.Provider value={shared}>
      {children}
    </MembershipContext.Provider>
  );
}

export const useMembershipContext = (): MembershipContextType => useContext(MembershipContext);
